import type { NodeInfo } from '../../api'
import { Link } from 'react-router'
import { nodePath, type NodeRelay } from './api'
import { ago, clock, spkiGroups, spkiShort } from './format'
import { RELAY_STATE_SPEC, relayNextStep } from './states'
import { Copy, ROLE_HINT, ROLE_LABEL, RoleGlyph, StateBadge } from './ui'

/*
 * Nodes: the relay key and peer list columns and the "Relay key" block of the
 * node card (design #r0 (c); handoff/nodes.md, KeyTab). Read-only in R0.
 * The private key never leaves the node; the console shows the SHA-256 of the
 * public key (SPKI) only.
 */

/**
 * "trusted by 2 of 3": how many of the other nodes of the current peer list
 * acknowledged a version that carries this node's current key (R0.9i).
 */
export function Trusted({ r, testId, long }: { r: NodeRelay; testId?: string; long?: boolean }) {
  if (r.peers_total === undefined || r.peers_acked === undefined || r.state === 'disabled' || r.state === 'unsupported' || r.state === 'revoked') return null
  if (r.peers_total === 0) {
    return (
      <span className="text-[11px] text-muted" data-testid={testId} data-acked="0" data-total="0">
        {long ? 'No other nodes in the peers list yet' : 'only node'}
      </span>
    )
  }
  const all = r.peers_acked >= r.peers_total
  return (
    <span
      className={`tabular font-mono text-[11px] ${all ? 'text-muted' : 'text-warn'}`}
      data-testid={testId}
      data-acked={r.peers_acked}
      data-total={r.peers_total}
      title="Other nodes of the peers list that acknowledged a version with this node's current key: they accept its legs"
    >
      {long ? `Trusted by ${r.peers_acked} of ${r.peers_total} other nodes` : `trusted ${r.peers_acked}/${r.peers_total}`}
      {!all && (long ? ': the rest have not acknowledged the current peers list yet' : '')}
    </span>
  )
}

/**
 * "Waiting for: ams-1, nyc-3" — the nodes that have not acknowledged a peers list
 * with this node's current key yet (NodeRelay.peers_pending, API-UI; ≤ 50). Absent
 * on a center without the field: then only the count of Trusted is known.
 */
export function Pending({ r, nameOf, testId }: { r: NodeRelay; nameOf: (id?: string) => string; testId?: string }) {
  const ids = r.peers_pending
  if (!ids?.length || r.state === 'disabled' || r.state === 'revoked') return null
  const more = r.peers_total !== undefined && r.peers_acked !== undefined ? r.peers_total - r.peers_acked - ids.length : 0
  return (
    <p className="mt-0.5 text-[11px] text-warn" data-testid={testId} data-ids={ids.join(',')}>
      Waiting for:{' '}
      {ids.map((id, i) => (
        <span key={id}>
          {i > 0 && ', '}
          <Link to={nodePath(id, 'key')} className="font-mono underline underline-offset-2" title={id}>
            {nameOf(id)}
          </Link>
          {r.peers_pending_offline?.includes(id) && (
            <span className="text-muted" data-testid={testId ? `${testId}-offline-${id}` : undefined}>
              {' '}
              (offline)
            </span>
          )}
        </span>
      ))}
      {more > 0 && <span className="text-muted"> +{more} more</span>}
    </p>
  )
}

/**
 * Why the node is not (fully) in the routing network and the action that fixes
 * it (PROV-RELAY-1, relayNextStep): Enable relay on the Host & agent tab (the
 * node keeps its id), revoke a stale node that never comes back, or wait.
 */
export function RelayNextStep({ n, centerOn = true, testId }: { n: NodeInfo; centerOn?: boolean; testId?: string }) {
  const r = n.relay
  const step = r ? relayNextStep(r, centerOn) : null
  if (!r || !step) return null
  return (
    <div data-testid={testId ?? `relay-next-step-${n.id}`} data-action={step.action} className="rounded-md border border-warn/50 bg-warn/[.07] px-3 py-2 text-xs">
      <p className="text-warn">▲ {step.why}</p>
      {step.action === 'enable_relay' && (
        <p className="mt-1 text-text-soft">
          <Link to={nodePath(n.id, 'host')} className="font-medium underline underline-offset-2" data-testid={`relay-next-enable-${n.id}`}>
            Host &amp; agent → Enable relay
          </Link>{' '}
          (your code): the agent turns RELAY on, the center registers the node’s key, the node keeps its id {n.id}. No SSH, no rebind.
        </p>
      )}
      {step.action === 'revoke_stale' && (
        <p className="mt-1 text-text-soft">
          Offline:{' '}
          {r.peers_pending_offline?.map((id, i) => (
            <span key={id}>
              {i > 0 && ', '}
              <Link to={nodePath(id, 'key')} className="font-mono underline underline-offset-2">
                {id}
              </Link>
            </span>
          ))}
          . If one of them is an old identity (a rebind or a deleted server), revoke it on its card.
        </p>
      )}
    </div>
  )
}

function Row({ k, children, testId }: { k: string; children: React.ReactNode; testId?: string }) {
  return (
    <>
      <dt className="text-muted">{k}</dt>
      <dd className="min-w-0" data-testid={testId}>
        {children}
      </dd>
    </>
  )
}

/** The "Relay key" block of the node card. */
export function RelayKeyBlock({ n, nameOf = (id) => id ?? '—' }: { n: NodeInfo; nameOf?: (id?: string) => string }) {
  const r: NodeRelay | undefined = n.relay
  if (!r) return null
  const spec = RELAY_STATE_SPEC[r.state] ?? RELAY_STATE_SPEC.unregistered
  return (
    <section data-testid={`relay-key-block-${n.id}`} className="flex flex-col gap-3 rounded-md border border-line-2 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-medium">Relay key</h3>
        <StateBadge spec={spec} testId={`relay-state-${n.id}`} />
        {n.roles && (
          <span className="ml-auto inline-flex flex-wrap items-center gap-2 text-text-soft" aria-label={`Roles: ${n.roles.map((x) => ROLE_LABEL[x]).join(', ')}`}>
            {n.roles.map((x) => (
              <span key={x} className="inline-flex items-center gap-1 text-xs" title={ROLE_HINT[x]}>
                <RoleGlyph role={x} />
                {ROLE_LABEL[x]}
              </span>
            ))}
          </span>
        )}
      </div>
      <RelayNextStep n={n} />
      <dl className="grid grid-cols-[minmax(96px,auto)_minmax(0,1fr)] gap-x-3 gap-y-2 text-[13px]">
        <Row k="Key (SPKI)" testId={`relay-spki-${n.id}`}>
          {r.spki ? (
            <span className="flex flex-col gap-1">
              <Copy value={r.spki} display={spkiShort(r.spki)} label="relay key" />
              <span className="tabular font-mono text-[11px] break-all text-muted">{spkiGroups(r.spki)}</span>
            </span>
          ) : (
            <span className="text-muted">No key registered: it comes with the join (install with relay) or with Enable relay on the Host &amp; agent tab.</span>
          )}
        </Row>
        <Row k="Next key" testId={`relay-next-${n.id}`}>
          {r.spki_next ? <Copy value={r.spki_next} display={spkiShort(r.spki_next)} label="next relay key" /> : <span className="text-xs text-muted">none announced</span>}
        </Row>
        <Row k="Changed" testId={`relay-changed-${n.id}`}>
          {r.spki_changed_at ? (
            <span className="tabular text-xs">
              {clock(r.spki_changed_at)} · {ago(r.spki_changed_at)}
            </span>
          ) : (
            <span className="text-xs text-muted">not since the join</span>
          )}
        </Row>
        <Row k="Peers list" testId={`relay-peers-${n.id}`}>
          <span className="num text-xs">
            v{r.peers_version_acked} of v{r.peers_version} ·{' '}
            {r.in_peers ? <span className="text-live">✓ in peers</span> : <span className="text-warn">○ not acknowledged yet</span>}
          </span>
          <span className="mt-0.5 block">
            <Trusted r={r} long testId={`relay-trusted-${n.id}`} />
          </span>
          <Pending r={r} nameOf={nameOf} testId={`relay-pending-${n.id}`} />
          {r.peers_rejected && (
            <p className="mt-1 text-xs text-warn">
              ▲ Refused peers list v{r.peers_rejected.version} ({r.peers_rejected.source}): {r.peers_rejected.reason} · {clock(r.peers_rejected.at)}
            </p>
          )}
        </Row>
        <Row k="Endpoints">
          {r.endpoints.length ? (
            <span className="flex flex-col gap-1">
              {r.endpoints.map((e) => (
                <Copy key={e} value={e} label="relay endpoint" />
              ))}
            </span>
          ) : (
            <span className="text-xs text-muted">none announced</span>
          )}
          <span className={`mt-0.5 block text-[11px] ${r.listen ? 'text-muted' : 'text-warn'}`}>{r.listen ? 'listener up' : 'listener down'}</span>
        </Row>
        <Row k="Protocol">
          <span className="font-mono text-xs text-text-soft">
            {r.versions?.length ? `relay v${r.versions.join(', ')}` : 'relay —'}
            {r.proto !== undefined && ` · ctl proto ${r.proto}`}
            {!!r.caps?.length && ` · ${r.caps.join(' ')}`}
          </span>
        </Row>
        <Row k="Routes">
          <span className="num text-xs">
            applied v{r.routes_version_acked} of v{r.routes_version}
            {r.routes_version_acked < r.routes_version && <span className="text-warn"> · not applied yet</span>}
          </span>
        </Row>
        {n.clock && (
          <Row k="Clock">
            <span className={`num text-xs ${n.clock.suspect ? 'text-warn' : ''}`}>
              {n.clock.offset_ms > 0 ? '+' : ''}
              {n.clock.offset_ms.toFixed(1)} ms vs the center
              {n.clock.suspect && ' · suspect: traces through this node are not trusted'}
            </span>
          </Row>
        )}
      </dl>
      <p className="text-[11px] text-muted">SHA-256 of the public key. The private key never leaves the node.</p>
    </section>
  )
}
